import { useState, useEffect } from 'react';
import { supabase } from '@saas-boilerplate/shared';
import { useAuth } from '@/contexts/auth-context';
import { toast } from 'sonner';
import { Loader2, Save, Bot, Bell, Type, AlignLeft, Settings2, X, ExternalLink, Calendar } from 'lucide-react';
import { invokeEdgeFn } from '@/lib/edge-functions';
import { buildCalendarSnippet } from '@/lib/calendar-snippet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from './ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Separator } from './ui/separator';

const MEETING_TYPES = [
  { value: 'standup', label: 'Stand-up' },
  { value: 'one_on_one', label: '1-on-1' },
  { value: 'team', label: 'Team meeting' },
  { value: 'retrospective', label: 'Retrospective' },
  { value: 'project_review', label: 'Project review' },
] as const;

export type SeriesForFlyout = {
  id: string;
  title: string;
  description?: string | null;
  meeting_type?: string | null;
  transcript_bot_enabled?: boolean | null;
  /** 'bot' = Recall notetaker dials in; 'desktop' = the Upstand desktop app records it, no bot. */
  capture_method?: string | null;
  email_notifications_enabled?: boolean | null;
  /** 'default' inherits the workspace mode; 'private' = owner + attendees only, even in a Shared workspace. */
  visibility?: 'default' | 'private' | null;
  /** Required for the "Insert orchestration block" action. */
  workspace_id?: string | null;
  /** 'google' | 'microsoft' | null. Null means no connected calendar - block insertion disabled. */
  calendar_provider?: 'google' | 'microsoft' | string | null;
  /** Recurring/series id on the calendar provider (Google recurringEventId, MS seriesMasterId). */
  calendar_series_id?: string | null;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  series: SeriesForFlyout | null;
  canManage: boolean;
  botName?: string | null;
  /** When true, the link points to the current user's own Personal settings. Otherwise it's read-only context. */
  botNameOwnedByCurrentUser?: boolean;
  /** In-person (ad-hoc desktop) meetings have no bot/orchestration/notifications - hide those sections. */
  isInPerson?: boolean;
  /** True when the workspace is Shared - only then does a per-meeting "Private" toggle make sense. */
  workspaceShared?: boolean;
  /** Whether the current user has the desktop app registered - drives the Desktop App option's hint. */
  desktopAppInstalled?: boolean;
  /** Admin switch (workspaces.settings.allow_auto_desktop_recording). Off = auto-record won't fire. */
  autoDesktopAllowed?: boolean;
  onSaved?: (updated: Partial<SeriesForFlyout>) => void;
};

export function SeriesSettingsFlyout({ open, onOpenChange, series, canManage, botName, botNameOwnedByCurrentUser, isInPerson, workspaceShared, desktopAppInstalled, autoDesktopAllowed, onSaved }: Props) {
  const { user } = useAuth();
  const [isPrivate, setIsPrivate] = useState(false);
  const [savingVisibility, setSavingVisibility] = useState(false);
  const displayBotName = (botName && botName.trim()) || 'Upstand Assistant';
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [meetingType, setMeetingType] = useState<string>('team');
  /**
   * How this series gets captured. One control over two columns: transcript_bot_enabled is whether
   * to capture at all, capture_method is how. 'off' is the old bot-disabled state.
   * See docs/PRD_DEVICE_AUTO_RECORD.md §3.
   */
  const [captureChoice, setCaptureChoice] = useState<'bot' | 'desktop' | 'off'>('bot');
  const [emailEnabled, setEmailEnabled] = useState(true);
  /**
   * Pre-meeting nudge toggle. Backed by meeting_notification_settings.pre_meeting_nudge_minutes
   * for the series-level row (meeting_id IS NULL). When toggled off we store null; when toggled
   * back on we restore the previously-saved minutes (defaulting to 120 if none was set).
   */
  const [preNudgeEnabled, setPreNudgeEnabled] = useState(true);
  const [preNudgeMinutes, setPreNudgeMinutes] = useState<number>(120);
  const [saving, setSaving] = useState(false);
  const [insertingBlock, setInsertingBlock] = useState(false);
  /**
   * Whether the current user is the calendar organizer for this series. Loaded when the flyout
   * opens. Drives the orchestration-block insertion button: Google/Microsoft only propagate
   * description edits to other attendees when the writer is the organizer, so we disable the
   * button otherwise to avoid silent no-ops.
   */
  const [isCalendarOrganizer, setIsCalendarOrganizer] = useState<boolean | null>(null);

  // Sync form state when the series changes or the flyout opens
  useEffect(() => {
    if (!series) return;
    setTitle(series.title || '');
    setDescription(series.description || '');
    setMeetingType(series.meeting_type || 'team');
    setCaptureChoice(
      !series.transcript_bot_enabled ? 'off' : series.capture_method === 'desktop' ? 'desktop' : 'bot',
    );
    setEmailEnabled(series.email_notifications_enabled !== false);
    setIsPrivate(series.visibility === 'private');
  }, [series?.id, open]);

  /** Per-meeting privacy in a Shared workspace: owner/admin restricts this series to owner + attendees.
   *  Backed by the set_meeting_series_visibility RPC (owner-or-admin enforced server-side). */
  const handleToggleVisibility = async (nextPrivate: boolean) => {
    if (!series?.id) return;
    const prev = isPrivate;
    setIsPrivate(nextPrivate); // optimistic
    setSavingVisibility(true);
    try {
      const { error } = await supabase.rpc('set_meeting_series_visibility', {
        p_series_id: series.id,
        p_visibility: nextPrivate ? 'private' : 'default',
      });
      if (error) throw error;
      onSaved?.({ visibility: nextPrivate ? 'private' : 'default' });
      toast.success(nextPrivate ? 'Meeting set to private' : 'Meeting visible to the workspace');
    } catch (err: any) {
      setIsPrivate(prev); // revert
      toast.error('Failed to update visibility', { description: err?.message });
    } finally {
      setSavingVisibility(false);
    }
  };

  // Load pre-meeting nudge state from meeting_notification_settings (series-level row).
  // The notifications_snapshot_trigger creates this row when a series is added, so it
  // should always exist by the time this flyout opens.
  useEffect(() => {
    if (!open || !series?.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('meeting_notification_settings')
        .select('pre_meeting_nudge_minutes')
        .eq('series_id', series.id)
        .is('meeting_id', null)
        .maybeSingle();
      if (cancelled) return;
      const minutes = (data as { pre_meeting_nudge_minutes: number | null } | null)?.pre_meeting_nudge_minutes ?? null;
      setPreNudgeEnabled(minutes != null);
      if (minutes != null) setPreNudgeMinutes(minutes);
    })();
    return () => {
      cancelled = true;
    };
  }, [series?.id, open]);

  // Detect calendar-organizer status: any meeting in this series where the user is an attendee
  // with is_organizer = true. Matches on auth user_id or email (case-insensitive).
  useEffect(() => {
    if (!open || !series?.id) {
      setIsCalendarOrganizer(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const email = (user?.email || '').trim().toLowerCase();
      const { data: meetingRows } = await supabase
        .from('meetings')
        .select('id')
        .eq('series_id', series.id);
      const meetingIds = ((meetingRows as { id: string }[] | null) || []).map((r) => r.id);
      if (cancelled) return;
      if (meetingIds.length === 0) {
        setIsCalendarOrganizer(false);
        return;
      }
      const { data: orgRows } = await supabase
        .from('meeting_attendees')
        .select('user_id, email')
        .in('meeting_id', meetingIds)
        .eq('is_organizer', true);
      if (cancelled) return;
      const matches = ((orgRows as { user_id: string | null; email: string | null }[] | null) || []).some(
        (r) =>
          (user?.id && r.user_id === user.id) ||
          (email && (r.email || '').trim().toLowerCase() === email),
      );
      setIsCalendarOrganizer(matches);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, series?.id, user?.id, user?.email]);

  /**
   * Insert (or re-insert) the orchestration block into the user's calendar invite for this series.
   * - Looks up the series' slug from `meeting_series_slugs` (registered at enrolment time).
   * - Picks any meeting from the series as the calendar_event_id (target for non-recurring or fallback).
   * - Calls `calendar-update-description` which de-dupes on the snippet intro line.
   */
  const handleInsertOrchestrationBlock = async () => {
    if (!series || insertingBlock) return;
    if (!series.workspace_id) {
      toast.error('Series is missing workspace info');
      return;
    }
    if (!series.calendar_provider) {
      toast.error('No calendar connected for this series', {
        description: 'Connect Google or Outlook in Integrations first.',
      });
      return;
    }

    setInsertingBlock(true);
    try {
      // 1. Resolve the series slug used in the direct link.
      const { data: slugRow, error: slugErr } = await supabase
        .from('meeting_series_slugs')
        .select('slug')
        .eq('series_id', series.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (slugErr) throw slugErr;
      const slug = (slugRow as { slug?: string } | null)?.slug;
      if (!slug) {
        toast.error('This series has no direct link slug', {
          description: 'Re-enroll the series to generate one.',
        });
        return;
      }

      // 2. Pick a calendar_event_id (any meeting in the series with one).
      const { data: meetingRow } = await supabase
        .from('meetings')
        .select('calendar_event_id, starts_at')
        .eq('series_id', series.id)
        .not('calendar_event_id', 'is', null)
        .order('starts_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      const calendarEventId = (meetingRow as { calendar_event_id?: string } | null)?.calendar_event_id;
      if (!calendarEventId && !series.calendar_series_id) {
        toast.error('No calendar event found for this series');
        return;
      }

      // 3. Build snippet and call edge function.
      const appBase =
        import.meta.env.VITE_APP_URL?.replace(/\/$/, '') ||
        (typeof window !== 'undefined' ? window.location.origin : '');
      const directLink = `${appBase}/meet/direct/${encodeURIComponent(slug)}`;
      const signupUrl = `${appBase}/signup`;
      const snippetText = buildCalendarSnippet(directLink, signupUrl);

      const targetType = series.calendar_series_id ? 'series' : 'event';
      const res = await invokeEdgeFn<{ ok?: boolean; already_inserted?: boolean }>(
        'calendar-update-description',
        {
          workspace_id: series.workspace_id,
          provider: series.calendar_provider,
          calendar_event_id: calendarEventId || series.calendar_series_id,
          calendar_series_id: series.calendar_series_id || null,
          target_type: targetType,
          description_snippet: snippetText,
        },
      );
      if (res?.already_inserted) {
        toast.info('Already in your calendar', {
          description: 'The orchestration block was already present on this event.',
        });
      } else {
        toast.success('Inserted into your calendar invite');
      }
    } catch (err: any) {
      toast.error('Insert failed', {
        description: err?.message || 'Try reconnecting your calendar in Integrations.',
      });
    } finally {
      setInsertingBlock(false);
    }
  };

  const handleSave = async () => {
    if (!series || !canManage) return;
    if (!title.trim()) { toast.error('Series name is required'); return; }

    setSaving(true);
    try {
      const updates = {
        title: title.trim(),
        description: description.trim() || null,
        meeting_type: meetingType,
        transcript_bot_enabled: captureChoice !== 'off',
        // Keep 'bot' when capture is off: capture_method is only meaningful while capturing, and
        // leaving a stale 'desktop' behind would silently suppress the bot if capture is re-enabled.
        capture_method: captureChoice === 'desktop' ? 'desktop' : 'bot',
        email_notifications_enabled: emailEnabled,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('meeting_series')
        .update(updates)
        .eq('id', series.id);

      if (error) throw error;

      // Pre-meeting nudge lives in meeting_notification_settings (series-level row).
      // The snapshot trigger always creates this row on series insert; if it's somehow
      // missing we don't block the save here - the dispatcher treats a missing row as
      // "no nudges configured".
      const { error: nudgeErr } = await supabase
        .from('meeting_notification_settings')
        .update({
          pre_meeting_nudge_minutes: preNudgeEnabled ? preNudgeMinutes : null,
          updated_at: new Date().toISOString(),
        })
        .eq('series_id', series.id)
        .is('meeting_id', null);
      if (nudgeErr) throw nudgeErr;

      toast.success('Series settings saved');
      onSaved?.({ ...updates, id: series.id });
      onOpenChange(false);
    } catch (err: any) {
      toast.error('Failed to save', { description: err?.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-[480px]">
        <SheetHeader className="border-b border-border/50 px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                <Settings2 className="h-4 w-4 text-primary" />
              </div>
              <div>
                <SheetTitle className="text-base font-semibold">Series settings</SheetTitle>
                <SheetDescription className="text-xs text-muted-foreground">
                  {series?.title || 'Meeting series'}
                </SheetDescription>
              </div>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="space-y-0">
            {/* Name & description */}
            <section className="space-y-4 px-6 py-5">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                <Type className="h-3.5 w-3.5" />
                Details
              </div>

              <div className="space-y-2">
                <Label htmlFor="series-title">Series name</Label>
                <Input
                  id="series-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Weekly team sync"
                  disabled={!canManage}
                  maxLength={120}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="series-description">
                  Description
                  <span className="ml-1.5 text-xs font-normal text-muted-foreground">(optional)</span>
                </Label>
                <Textarea
                  id="series-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What is this meeting series for?"
                  rows={3}
                  disabled={!canManage}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="series-type">Meeting type</Label>
                <Select value={meetingType} onValueChange={setMeetingType} disabled={!canManage}>
                  <SelectTrigger id="series-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MEETING_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Per-meeting privacy - only meaningful in a Shared workspace. */}
              {workspaceShared && (
                <div className="flex items-start justify-between gap-4 pt-1">
                  <div className="space-y-0.5">
                    <Label htmlFor="series-private" className="text-sm font-medium">
                      Private
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Only the owner and attendees can see this meeting. Off = visible to the whole workspace.
                    </p>
                  </div>
                  <Switch
                    id="series-private"
                    checked={isPrivate}
                    onCheckedChange={handleToggleVisibility}
                    disabled={!canManage || savingVisibility}
                    className="mt-0.5 shrink-0"
                  />
                </div>
              )}
            </section>

            {!isInPerson && (
            <>
            <Separator />

            {/* Capture settings */}
            <section className="space-y-4 px-6 py-5">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                <Bot className="h-3.5 w-3.5" />
                Recording
              </div>

              <div className="space-y-0.5">
                <Label className="text-sm font-medium">How this meeting is recorded</Label>
                <p className="text-xs text-muted-foreground">
                  Applies to every future meeting in this series.
                </p>
              </div>

              <RadioGroup
                value={captureChoice}
                onValueChange={(v) => setCaptureChoice(v as 'bot' | 'desktop' | 'off')}
                className="flex flex-col gap-2"
                disabled={!canManage}
              >
                <label className="flex cursor-pointer items-start gap-2.5 rounded-md border border-border/50 px-3 py-2.5 hover:border-border">
                  <RadioGroupItem value="bot" className="mt-0.5" disabled={!canManage} />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-foreground">Notetaker bot</span>
                    <span className="block text-xs text-muted-foreground">
                      {displayBotName} joins the call as a participant and captures the transcript. Works
                      even when nobody has the desktop app open.
                    </span>
                  </span>
                </label>

                <label className="flex cursor-pointer items-start gap-2.5 rounded-md border border-border/50 px-3 py-2.5 hover:border-border">
                  <RadioGroupItem value="desktop" className="mt-0.5" disabled={!canManage} />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-foreground">Desktop App</span>
                    <span className="block text-xs text-muted-foreground">
                      No bot joins the call. The Upstand desktop app records the meeting from an
                      attendee's computer and starts on its own when the call is detected.
                    </span>
                    <span className="mt-1 block text-[11px] text-muted-foreground/80">
                      Nothing is recorded if no attendee has the app running, and in-call check-in
                      playback is not available without the bot.
                    </span>
                    {captureChoice === 'desktop' && autoDesktopAllowed === false && (
                      <span className="mt-1.5 block text-[11px] text-red-600 dark:text-red-400">
                        Automatic desktop recording is turned off for this workspace, so this meeting
                        will not record itself. An admin can allow it in Organisation settings.
                      </span>
                    )}
                    {captureChoice === 'desktop' && autoDesktopAllowed !== false && desktopAppInstalled === false && (
                      <span className="mt-1.5 block text-[11px] text-muted-foreground">
                        You do not have the desktop app yet.{' '}
                        <a href="/download" className="underline underline-offset-2 hover:text-foreground">
                          Get the Mac app
                        </a>
                      </span>
                    )}
                  </span>
                </label>

                <label className="flex cursor-pointer items-start gap-2.5 rounded-md border border-border/50 px-3 py-2.5 hover:border-border">
                  <RadioGroupItem value="off" className="mt-0.5" disabled={!canManage} />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-foreground">Don't record</span>
                    <span className="block text-xs text-muted-foreground">
                      No transcript, no insights, no recap. Agendas, notes and action items still work.
                    </span>
                  </span>
                </label>
              </RadioGroup>

              {captureChoice === 'bot' && (
              <div className="flex items-center justify-between gap-3 rounded-md border border-border/50 bg-muted/30 px-3 py-2">
                <div className="flex min-w-0 flex-col">
                  <span className="text-[11px] uppercase tracking-wider text-muted-foreground/70">Bot name</span>
                  <span className="truncate text-sm text-muted-foreground">{displayBotName}</span>
                  <span className="text-[11px] text-muted-foreground/70">
                    {botNameOwnedByCurrentUser
                      ? 'From your Personal settings'
                      : 'From the series owner’s Personal settings'}
                  </span>
                </div>
                {botNameOwnedByCurrentUser && (
                  <a
                    href="/settings"
                    className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                  >
                    Personal settings
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
              )}
            </section>

            <Separator />

            {/* Calendar invite - manual re-insertion of the orchestration block */}
            <section className="space-y-3 px-6 py-5">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                <Calendar className="h-3.5 w-3.5" />
                Calendar invite
              </div>

              <p className="text-xs text-muted-foreground">
                Adds the orchestration block to the body of your calendar invite so attendees see the workspace
                link. Safe to re-run - duplicates are skipped automatically.
              </p>

              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => void handleInsertOrchestrationBlock()}
                disabled={
                  !canManage ||
                  insertingBlock ||
                  !series?.calendar_provider ||
                  isCalendarOrganizer === false
                }
                className="gap-2"
              >
                {insertingBlock ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Calendar className="h-3.5 w-3.5" />
                )}
                Insert orchestration block
              </Button>

              {!series?.calendar_provider && (
                <p className="text-[11px] italic text-muted-foreground">
                  No calendar connected for this series - connect Google or Outlook in Integrations to enable.
                </p>
              )}

              {series?.calendar_provider && isCalendarOrganizer === false && (
                <p className="text-[11px] italic text-muted-foreground">
                  Only the calendar organizer can edit this invite&apos;s description so it reaches every
                  attendee. Ask the organizer to enrol the meeting on Upstand and insert the block.
                </p>
              )}
            </section>

            <Separator />

            {/* Email settings */}
            <section className="space-y-4 px-6 py-5">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                <Bell className="h-3.5 w-3.5" />
                Notifications
              </div>

              <div className="flex items-start justify-between gap-4">
                <div className="space-y-0.5">
                  <Label htmlFor="pre-nudge-enabled" className="text-sm font-medium">
                    Pre-meeting nudges
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Email attendees ahead of each meeting with the agenda and any outstanding actions.
                  </p>
                </div>
                <Switch
                  id="pre-nudge-enabled"
                  checked={preNudgeEnabled}
                  onCheckedChange={setPreNudgeEnabled}
                  disabled={!canManage}
                  className="mt-0.5 shrink-0"
                />
              </div>

              <div className="flex items-start justify-between gap-4">
                <div className="space-y-0.5">
                  <Label htmlFor="email-enabled" className="text-sm font-medium">
                    Post-meeting summaries
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Send attendees a summary with decisions, action items, and insights after each meeting - over their preferred channel.
                  </p>
                </div>
                <Switch
                  id="email-enabled"
                  checked={emailEnabled}
                  onCheckedChange={setEmailEnabled}
                  disabled={!canManage}
                  className="mt-0.5 shrink-0"
                />
              </div>
            </section>
            </>
            )}
          </div>
        </div>

        {/* Footer */}
        {canManage && (
          <div className="border-t border-border/50 px-6 py-4">
            <div className="flex items-center justify-end gap-3">
              <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={saving}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving || !title.trim()}>
                {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />}
                Save changes
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
